import store from '../index'
import {
  GoogleAccountTaskSection,
  Language,
  MainTaskType,
  OrderedTaskStep,
  PublishTaskSection,
  Task,
  TaskState,
  TaskStep,
  UpdateTaskSection,
  VerificationTaskSection
} from './initialState'
import {
  allowGoogleAccountInVerification,
  checkAgeLimits,
  numberTaskSteps,
  setDefaultLangFirst
} from '../../utils/helpers'
import {axiosV3} from '../../axios'
import Notification from '../../components/notification/Notification'
import i18n from 'i18next'
import {setGoogleAdminEmail, setSelectedTask, setStartStepIndex, updateAvailablePhone} from './actions'
import {PublisherTaskType} from '../taskList/initialState'
import {get} from 'idb-keyval'


type Response<T> = {
  data: T
  meta: { [key: string]: any }
}

export async function takeTaskToProcess({id, start = false}: { id: string, start?: boolean }) {
  //  Взятие задачи в работу (перевод в статус "process")
  //  Перевод задачи на обновление с покупками в статус "process" после модерации покупок

  const inclusions = [
    'status',
    'task.type',
    'task.purchases',
    'application.type',
    'application.category',
    'application.status',
    'application.locales',
    'application.default_locale',
    'application.restriction'
  ].join(',')

  try {

    const response: Response<Task> = await axiosV3.post(`subtasks/${id}/process?include=${inclusions}`)

    const task = {
      ...response.data,
      ...response.meta
    }
    await store.dispatch(setSelectedTask(task))
    if (start) {
      store.dispatch(setStartStepIndex(id))
    }
    return true
  } catch (e) {
    if (e.logout || e.offline) return
    if (e.response?.status === 403) {
      Notification({noticeType: 'warn', text: i18n.t('Not available task other user')})
      return
    }
    Notification({noticeType: 'warn', text: i18n.t('Take task error')})
    console.info('Error in the method "takeTaskToProcess": ', e)
  }
}

export async function downloadTaskData(id: string | number) {
  //  Получение с бэка данных по задаче

  const inclusions = [
    'status',
    'task.type',
    'task.purchases',
    'application.type',
    'application.category',
    'application.status',
    'application.locales',
    'application.default_locale',
    'application.restriction'
  ].join(',')

  try {
    const response: Response<Task> = await axiosV3.get(`subtasks/${id}?include=${inclusions}`)

    const task = {...response.data, ...response.meta}
    const failedTask = await get('failedTask')
    if (task.type === PublisherTaskType.VERIFICATION) {
      store.dispatch(updateAvailablePhone(failedTask !== task.id ? !!task?.google_account_password : false))
    }
    if (task.type === PublisherTaskType.PUBLICATION) task.showReserveEmailSteps = await getGoogleAdminEmail(task.id)
    store.dispatch(setSelectedTask(task))
  } catch (e) {
    if (e.logout || e.offline) return
    Notification({noticeType: 'warn', text: i18n.t('Download task error')})
    console.info('Error in the method "downloadTaskData": ', e)
  }
}

export function getStepsForVerificationTask(): Array<OrderedTaskStep> {
  //  Формирование массива с настройками шагов инструкции для задачи на верификацию аккаунта

  const isShowGoogleAccountsSteps: boolean = allowGoogleAccountInVerification()

  let steps: Array<TaskStep>
  const localeName: string = store.getState().locale.locale.name

  const getPassportStep = (): Array<TaskStep> => {
    const showAdditionalStep: boolean = store.getState().types.needUploadPassport
    if (!showAdditionalStep) return []
    return [{
      componentPath: 'verification/Guide4_1',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE,
        linkId: 'passport',
        blockExecution: true,
        stepsToBeCompleted: ['compensation']
      }
    }]
  }

  const getVerificationGuides = (): Array<TaskStep> => {
    if (localeName !== 'ru') return []

    return [
      {
        componentPath: 'verification/VerificationGuide1',
        stepOptions: {
          section: VerificationTaskSection.REG_IN_GOOGLE,
          stepsToBeCompleted: ['compensation']
        }
      },
      {
        componentPath: 'verification/VerificationGuide2',
        stepOptions: {
          section: VerificationTaskSection.REG_IN_GOOGLE,
          stepsToBeCompleted: ['compensation']
        }
      }
    ]
  }

  steps = [
    {
      componentPath: 'verification/Guide1_0',
      stepOptions: {
        section: VerificationTaskSection.NONE
      }
    },
    {
      componentPath: 'verification/Guide1_1',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    {
      componentPath: 'verification/Guide1_2',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    {
      componentPath: 'verification/Guide2_1',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    {
      componentPath: 'verification/Guide2_2',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    {
      componentPath: 'verification/Guide3',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    {
      componentPath: 'verification/Guide3_1',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    {
      componentPath: 'verification/Guide3_2',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    {
      componentPath: 'verification/Guide4',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    {
      componentPath: 'verification/Guide5',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    {
      componentPath: 'verification/Guide6',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    {
      componentPath: 'verification/Guide7',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    {
      componentPath: 'verification/Guide8',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    {
      componentPath: 'verification/Guide9',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    {
      componentPath: 'verification/Guide9_1',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    {
      componentPath: 'verification/Guide9_2',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    {
      componentPath: 'verification/Guide9_3',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    {
      componentPath: 'verification/Guide10',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE,
        linkId: 'compensation',
        blockExecution: true
      }
    },
    {
      componentPath: 'verification/Guide11',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE,
        stepsToBeCompleted: ['compensation']
      }
    },
    {
      componentPath: 'verification/Guide12',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE
      }
    },
    // ...getVerificationGuides(),
    // ...getPassportStep(),
    {
      componentPath: 'verification/Guide13',
      stepOptions: {
        section: VerificationTaskSection.REG_IN_GOOGLE,
        linkId: 'verification',
        blockExecution: true,
        isLastStep: !isShowGoogleAccountsSteps,
        stepsToBeCompleted: ['compensation', 'passport']
      }
    },
    ...getRegistrationGuide(VerificationTaskSection, allowGoogleAccountInVerification()),
    ...getPasswdGuide(VerificationTaskSection, allowGoogleAccountInVerification())
  ]

  return numberTaskSteps(VerificationTaskSection, steps)
}

export function getStepsForPublishTask(task: Task): Array<OrderedTaskStep> {
  //  Формирование массива с настройками шагов инструкции для задачи на публикацию

  const ageIsLess13: boolean = checkAgeLimits(13, 'less', task.application.age_limit)
  const hasCSV: boolean = !!task.application.data_safety_file
  const showReserveEmailSteps: boolean = !!task.showReserveEmailSteps
  const userWhatsapp = store.getState().user.userData.whatsapp

  let steps: Array<TaskStep>
  const sortedLanguage: Array<Language> = setDefaultLangFirst(task.application.locales, task.application.default_locale)

  const getLocaleSteps = (): Array<TaskStep> => {
    const rawLocaleSteps = sortedLanguage.map((lang: Language, index: number): Array<TaskStep> => {
      let nextLangSteps: Array<TaskStep> = []
      if (index > 0) {
        nextLangSteps = [
          {
            componentPath: 'publish/Guide17',
            stepOptions: {
              section: PublishTaskSection.CREATE_APP
            }
          },
          {
            componentPath: 'publish/Guide18',
            stepOptions: {
              section: PublishTaskSection.CREATE_APP,
              langName: lang.name
            }
          }
        ]
      }

      return [
        ...nextLangSteps,
        {
          componentPath: 'publish/Guide11',
          stepOptions: {
            section: PublishTaskSection.CREATE_APP,
            langName: lang.name
          }
        },
        {
          componentPath: 'publish/Guide12',
          stepOptions: {
            section: PublishTaskSection.CREATE_APP,
            langName: lang.name
          }
        },
        {
          componentPath: 'publish/Guide13',
          stepOptions: {
            section: PublishTaskSection.CREATE_APP,
            langName: lang.name
          }
        },
        {
          componentPath: 'publish/Guide14',
          stepOptions: {
            section: PublishTaskSection.CREATE_APP,
            langName: lang.name
          }
        },
        {
          componentPath: 'publish/Guide15',
          stepOptions: {
            section: PublishTaskSection.CREATE_APP,
            langName: lang.name
          }
        },
        {
          componentPath: 'publish/Guide16',
          stepOptions: {
            section: PublishTaskSection.CREATE_APP
          }
        }
      ]
    })
    return rawLocaleSteps.flat()
  }
  const getCreateAppSteps = (): Array<TaskStep> => {
    return [
      {
        componentPath: 'publish/createApp/step1',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP,
          linkId: "instruction"
        }
      },
      {
        componentPath: 'publish/createApp/step2',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step3',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step4',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step5',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step6',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step7',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step8',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step9',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step10',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step11',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step12',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step13',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step14',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step15',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step16',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step17',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step18',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step19',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step20',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step21',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step22',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
      {
        componentPath: 'publish/createApp/step23',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP
        }
      },
    ]
  }

  const getDashboardSteps = (): Array<TaskStep> => {
    return [
      {
        componentPath: 'publish/dashboard/step24',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step25',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step26',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step27',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step28',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step29',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step30',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step31',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step32',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step33',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step34',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step35',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step36',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step37',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step38',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step39',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step40',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step41',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step42',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step43',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step44',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step45',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step46',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step47',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step48',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step48.1',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
      {
        componentPath: 'publish/dashboard/step48.2',
        stepOptions: {
          section: PublishTaskSection.DASHBOARD
        }
      },
    ]
  }
  const getTestingSteps = (): Array<TaskStep> => {
    return [
      {
        componentPath: 'publish/testing/step49',
        stepOptions: {
          section: PublishTaskSection.TESTING
        }
      },
      {
        componentPath: 'publish/testing/step50',
        stepOptions: {
          section: PublishTaskSection.TESTING
        }
      },
      {
        componentPath: 'publish/testing/step51',
        stepOptions: {
          section: PublishTaskSection.TESTING
        }
      },
      {
        componentPath: 'publish/testing/step52',
        stepOptions: {
          section: PublishTaskSection.TESTING
        }
      },
      {
        componentPath: 'publish/testing/step53',
        stepOptions: {
          section: PublishTaskSection.TESTING
        }
      },
      {
        componentPath: 'publish/testing/step54',
        stepOptions: {
          section: PublishTaskSection.TESTING
        }
      },
      {
        componentPath: 'publish/testing/step55',
        stepOptions: {
          section: PublishTaskSection.TESTING
        }
      },
      {
        componentPath: 'publish/testing/step56',
        stepOptions: {
          section: PublishTaskSection.TESTING
        }
      },
      {
        componentPath: 'publish/testing/step57',
        stepOptions: {
          section: PublishTaskSection.TESTING
        }
      },
      {
        componentPath: 'publish/Guide47',
        stepOptions: {
          section: PublishTaskSection.TESTING,
          blockExecution: true
        }
      },
    ]
  }
  const getTestersListSteps = (): Array<TaskStep> => {
    return [
      {
        componentPath: 'publish/testersList/step58',
        stepOptions: {
          section: PublishTaskSection.TESTERS_LIST
        }
      },
      {
        componentPath: 'publish/testersList/step59',
        stepOptions: {
          section: PublishTaskSection.TESTERS_LIST
        }
      },
      {
        componentPath: 'publish/testersList/step60',
        stepOptions: {
          section: PublishTaskSection.TESTERS_LIST
        }
      },
      {
        componentPath: 'publish/testersList/step61',
        stepOptions: {
          section: PublishTaskSection.TESTERS_LIST
        }
      },
      {
        componentPath: 'publish/testersList/step62',
        stepOptions: {
          section: PublishTaskSection.TESTERS_LIST
        }
      },
    ]
  }
  const getAddMailSteps = (): Array<TaskStep> => {
    if (!showReserveEmailSteps) {
      return []
    }
    return [
      {
        componentPath: 'publish/addMail/step63',
        stepOptions: {
          section: PublishTaskSection.ADD_MAIL
        }
      },
      {
        componentPath: 'publish/addMail/step64',
        stepOptions: {
          section: PublishTaskSection.ADD_MAIL
        }
      },
      {
        componentPath: 'publish/addMail/step65',
        stepOptions: {
          section: PublishTaskSection.ADD_MAIL
        }
      },
      {
        componentPath: 'publish/Guide54',
        stepOptions: {
          section: PublishTaskSection.ADD_MAIL,
          blockExecution: true
        }
      }
    ]
  }
  const getAfterTestersApproveSteps = (): Array<TaskStep> => {
    return [
      {
        componentPath: 'publish/afterTestersApprove/stepRequestTesters',
        stepOptions: {
          section: PublishTaskSection.AFTER_TESTERS_APPROVE,
          linkId: "afterTestersApprove"
        }
      },
      {
        componentPath: 'publish/afterTestersApprove/stepWaitTesters',
        stepOptions: {
          section: PublishTaskSection.AFTER_TESTERS_APPROVE
        }
      },
      {
        componentPath: 'publish/afterTestersApprove/step66',
        stepOptions: {
          section: PublishTaskSection.AFTER_TESTERS_APPROVE
        }
      },
      {
        componentPath: 'publish/afterTestersApprove/step67',
        stepOptions: {
          section: PublishTaskSection.AFTER_TESTERS_APPROVE
        }
      },
      {
        componentPath: 'publish/afterTestersApprove/step68',
        stepOptions: {
          section: PublishTaskSection.AFTER_TESTERS_APPROVE
        }
      },
      {
        componentPath: 'publish/afterTestersApprove/step69',
        stepOptions: {
          section: PublishTaskSection.AFTER_TESTERS_APPROVE,
        }
      },
      {
        componentPath: 'publish/afterTestersApprove/step70',
        stepOptions: {
          section: PublishTaskSection.AFTER_TESTERS_APPROVE,
          linkId: 'moderation',
          isLastStep: true,
          blockExecution: true
        }
      },
    ]
  }
  const getAddReserveEmailSteps = (): Array<TaskStep> => {
    return [
      {
        componentPath: 'publish/Guide48',
        stepOptions: {
          section: showReserveEmailSteps ? PublishTaskSection.AFTER_TESTERS_APPROVE : null,
        }
      },
      {
        componentPath: 'publish/Guide49',
        stepOptions: {
          section: showReserveEmailSteps ? PublishTaskSection.AFTER_TESTERS_APPROVE : null,
        }
      },
      {
        componentPath: 'publish/Guide50',
        stepOptions: {
          section: showReserveEmailSteps ? PublishTaskSection.AFTER_TESTERS_APPROVE : null,
        }
      },
      {
        componentPath: 'publish/Guide51',
        stepOptions: {
          section: showReserveEmailSteps ? PublishTaskSection.AFTER_TESTERS_APPROVE : null,
        }
      },
      {
        componentPath: 'publish/Guide52',
        stepOptions: {
          section: showReserveEmailSteps ? PublishTaskSection.AFTER_TESTERS_APPROVE : null,
        }
      },
      {
        componentPath: 'publish/Guide53',
        stepOptions: {
          section: showReserveEmailSteps ? PublishTaskSection.AFTER_TESTERS_APPROVE : null,
        }
      },
      {
        componentPath: 'publish/Guide54',
        stepOptions: {
          section: showReserveEmailSteps ? PublishTaskSection.AFTER_TESTERS_APPROVE : null,
          isLastStep: true,
          linkId: 'moderation',
          blockExecution: true
        }
      }
    ]
  }

  const getInstallExtensionSteps = (): Array<TaskStep> => {
    return [
      {
        componentPath: 'publish/installExtension/extension-step1',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP,
          linkId: "extension"
        }
      },
      {
        componentPath: 'publish/installExtension/extension-step2',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP,
        }
      },
      {
        componentPath: 'publish/installExtension/extension-step3',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP,
        }
      },
      {
        componentPath: 'publish/installExtension/extension-step4',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP,
        }
      },
      {
        componentPath: 'publish/installExtension/extension-step5',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP,
        }
      },
      {
        componentPath: 'publish/installExtension/extension-step6',
        stepOptions: {
          section: PublishTaskSection.CREATE_APP,
          blockExecution: true,
        },
      },
    ]
  }

  steps = [
    {
      componentPath: 'publish/Guide55',
      stepOptions: {
        section: !userWhatsapp ? PublishTaskSection.GO_TO_GOOGLE : null,
        blockExecution: true
      }
    },
    {
      componentPath: 'Guide1',
      stepOptions: {
        section: PublishTaskSection.GO_TO_GOOGLE,
        blockExecution: true
      }
    },
    {
      componentPath: 'publish/GuideOrAuto',
      stepOptions: {
        section: PublishTaskSection.CREATE_APP,
        blockExecution: true
      }
    },
    ...getInstallExtensionSteps(),
    ...getCreateAppSteps(),
    ...getDashboardSteps(),
    ...getTestingSteps(),
    ...getTestersListSteps(),
    ...getAddMailSteps(),
    ...getAfterTestersApproveSteps(),
    // ...getAddReserveEmailSteps(),
    // ...getLocaleSteps(),
  ]

  return numberTaskSteps(PublishTaskSection, steps)
}

export function getStepsForUpdateTask(task: Task): Array<OrderedTaskStep> {
  //  Формирование массива с настройками шагов инструкции для задачи на обновление
  const hasCSV: boolean = !!task.application.data_safety_file

  const addingCountries = task.calcAddingCountries
  const removingCountries = task.calcRemovingCountries
  const isEditCountries = !!addingCountries?.length || !!removingCountries?.length
  const mainTaskType = task.task?.type?.name

  const isPurchases = mainTaskType === MainTaskType.UPDATE_PURCHASES

  let steps: Array<TaskStep>
  const getLocaleSteps = (): Array<TaskStep> => {
    const sortedLanguage: Array<Language> = setDefaultLangFirst(task.application.locales, task.application.default_locale)
    const rawLocaleSteps = sortedLanguage.map((lang: Language, index: number): Array<TaskStep> => {
      let additionalSteps: Array<TaskStep>

      if (index === 0) {
        additionalSteps = [
          {
            componentPath: 'update/appDescription/step3',
            stepOptions: {
              section: UpdateTaskSection.APP_DESCRIPTION
            }
          }
        ]
      } else {
        additionalSteps = [
          {
            componentPath: 'update/appDescription/step9',
            stepOptions: {
              section: UpdateTaskSection.APP_DESCRIPTION
            }
          },
          {
            componentPath: 'update/appDescription/step10',
            stepOptions: {
              section: UpdateTaskSection.APP_DESCRIPTION,
              langName: lang.name
            }
          },
          {
            componentPath: 'update/appDescription/step11',
            stepOptions: {
              section: UpdateTaskSection.APP_DESCRIPTION,
              langName: lang.name
            }
          }
        ]
      }

      return [
        ...additionalSteps,
        {
          componentPath: 'update/appDescription/step4',
          stepOptions: {
            section: UpdateTaskSection.APP_DESCRIPTION,
            langName: lang.name
          }
        },
        {
          componentPath: 'update/appDescription/step4_1',
          stepOptions: {
            section: UpdateTaskSection.APP_DESCRIPTION
          }
        },
        {
          componentPath: 'update/appDescription/step5',
          stepOptions: {
            section: UpdateTaskSection.APP_DESCRIPTION,
            langName: lang.name
          }
        },
        {
          componentPath: 'update/appDescription/step5_1',
          stepOptions: {
            section: UpdateTaskSection.APP_DESCRIPTION
          }
        },
        {
          componentPath: 'update/appDescription/step6',
          stepOptions: {
            section: UpdateTaskSection.APP_DESCRIPTION,
            langName: lang.name
          }
        },
        {
          componentPath: 'update/appDescription/step6_1',
          stepOptions: {
            section: UpdateTaskSection.APP_DESCRIPTION
          }
        },
        {
          componentPath: 'update/appDescription/step7',
          stepOptions: {
            section: UpdateTaskSection.APP_DESCRIPTION,
            langName: lang.name
          }
        },
        {
          componentPath: 'update/appDescription/step7_1',
          stepOptions: {
            section: UpdateTaskSection.APP_DESCRIPTION
          }
        },
        {
          componentPath: 'update/appDescription/step8',
          stepOptions: {
            section: UpdateTaskSection.APP_DESCRIPTION,
            langName: lang.name
          }
        },
        {
          componentPath: 'update/appDescription/step8_1',
          stepOptions: {
            section: UpdateTaskSection.APP_DESCRIPTION,
            langName: lang.name
          }
        }
      ]
    })
    return [
      ...rawLocaleSteps.flat(),
      {
        componentPath: 'update/appDescription/stepReviewChangesBeforeSave',
        stepOptions: {
          section: UpdateTaskSection.APP_DESCRIPTION
        }
      },
      {
        componentPath: 'update/appDescription/stepSaveAndPublishForReview',
        stepOptions: {
          section: UpdateTaskSection.APP_DESCRIPTION
        }
      }
    ]
  }

  const getApkSteps = (): Array<TaskStep> => [
    {
      componentPath: 'update/appVersions/step13',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step14',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step15',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step16',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step17',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    }
  ]

  const getPurchaseSteps = (): Array<TaskStep> => [
    {
      componentPath: 'update/appVersions/step20',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step21',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step22',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step23',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step24',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step25',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step26',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step27',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step28',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step28_1',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step28_2',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step29',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step29_1',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step29_2',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step36',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step37',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step38',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step39',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step40',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step41',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step31',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step32',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step33',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    },
    {
      componentPath: 'update/appVersions/step34',
      stepOptions: {
        section: UpdateTaskSection.APP_VERSIONS
      }
    }
  ]

  const getCsvSteps = (): Array<TaskStep> => {
    switch (mainTaskType) {
      case MainTaskType.UPDATE_ASSEMBLY:
      case MainTaskType.UPDATE_ASO_IMG_ASSEMBLY:
      case MainTaskType.UPDATE_PURCHASES:
        const typeSection = mainTaskType === MainTaskType.UPDATE_ASO_IMG_ASSEMBLY ? 'APP_DESCRIPTION' : 'APP_VERSIONS'
        return [
          {
            componentPath: 'csv/CsvGuide_1',
            stepOptions: {
              section: hasCSV ? UpdateTaskSection[typeSection] : null
            }
          },
          {
            componentPath: 'csv/CsvGuide_2',
            stepOptions: {
              section: hasCSV ? UpdateTaskSection[typeSection] : null
            }
          },
          {
            componentPath: 'csv/CsvGuide_3',
            stepOptions: {
              section: hasCSV ? UpdateTaskSection[typeSection] : null
            }
          },
          {
            componentPath: 'csv/CsvGuide_4',
            stepOptions: {
              section: hasCSV ? UpdateTaskSection[typeSection] : null
            }
          },
          {
            componentPath: 'csv/CsvGuide_5',
            stepOptions: {
              section: hasCSV ? UpdateTaskSection[typeSection] : null
            }
          },
          {
            componentPath: 'csv/CsvGuide_6',
            stepOptions: {
              section: hasCSV ? UpdateTaskSection[typeSection] : null
            }
          },
          {
            componentPath: 'csv/CsvGuide_7',
            stepOptions: {
              section: hasCSV ? UpdateTaskSection[typeSection] : null
            }
          }
        ]
      default:
        return []
    }
  }

  const sectionUpdate = [
    MainTaskType.UPDATE_ASSEMBLY,
    MainTaskType.UPDATE_ASO_ASSEMBLY,
    MainTaskType.UPDATE_PURCHASES
  ].includes(mainTaskType)
    ? UpdateTaskSection.APP_VERSIONS
    : UpdateTaskSection.APP_DESCRIPTION

  const updateSectionFolder = sectionUpdate === UpdateTaskSection.APP_VERSIONS
    ? 'appVersions'
    : 'appDescription'

  const getEditCountriesSteps = (): Array<TaskStep> => {
    return [
      {
        componentPath: `update/${updateSectionFolder}/step48`,
        stepOptions: {
          section: !!removingCountries?.length ? sectionUpdate : null
        }
      },
      {
        componentPath: `update/${updateSectionFolder}/step49`,
        stepOptions: {
          section: !!removingCountries?.length ? sectionUpdate : null,
          isLastStep: isPurchases && !addingCountries?.length
        }
      },
      {
        componentPath: `update/${updateSectionFolder}/step50`,
        stepOptions: {
          section: !!addingCountries?.length && !!removingCountries?.length ? sectionUpdate : null
        }
      },
      {
        componentPath: `update/${updateSectionFolder}/step46`,
        stepOptions: {
          section: !!addingCountries?.length && !removingCountries?.length ? sectionUpdate : null
        }
      },
      {
        componentPath: `update/${updateSectionFolder}/step47`,
        stepOptions: {
          section: !!addingCountries?.length ? sectionUpdate : null,
          isLastStep: isPurchases
        }
      }
    ]
  }

  const getVariableSteps = (): Array<TaskStep> => {
    switch (mainTaskType) {
      case MainTaskType.UPDATE_ASO_TEXT_IMG:
      case MainTaskType.UPDATE_IMG:
        return getLocaleSteps()
      case MainTaskType.UPDATE_ASO_IMG:
        return [
          ...getEditCountriesSteps(),
          ...getLocaleSteps()
        ]
      case MainTaskType.UPDATE_ASSEMBLY:
        return [
          ...getEditCountriesSteps(),
          ...getCsvSteps(),
          ...getApkSteps()
        ]
      case MainTaskType.UPDATE_ASO_ASSEMBLY:
        return getApkSteps()
      case MainTaskType.UPDATE_ASO_IMG_ASSEMBLY:
        return [
          ...getEditCountriesSteps(),
          ...getCsvSteps(),
          ...getLocaleSteps(),
          ...getApkSteps()
        ]
      case MainTaskType.UPDATE_PURCHASES:
        return [
          ...getCsvSteps(),
          ...getPurchaseSteps()
        ]
      default:
        return []
    }
  }

  const getLastSteps = (): Array<TaskStep> => {
    if (isPurchases) {
      return [
        {
          componentPath: 'update/appVersions/step35',
          stepOptions: {
            section: UpdateTaskSection.APP_VERSIONS,
            isLastStep: !isEditCountries && !hasCSV,
            linkId: 'moderation',
            blockExecution: true
          }
        },
        {
          componentPath: 'update/appVersions/step44',
          stepOptions: {
            section: hasCSV ? UpdateTaskSection.APP_VERSIONS : null
          }
        },
        {
          componentPath: 'update/appVersions/step45',
          stepOptions: {
            section: hasCSV ? UpdateTaskSection.APP_VERSIONS : null,
            isLastStep: !isEditCountries && hasCSV,
            linkId: 'moderation',
            blockExecution: true
          }
        },
        ...getEditCountriesSteps()
      ]
    }

    const section: UpdateTaskSection = [
      MainTaskType.UPDATE_ASO_IMG,
      MainTaskType.UPDATE_ASO_TEXT_IMG,
      MainTaskType.UPDATE_IMG
    ].includes(mainTaskType)
      ? UpdateTaskSection.APP_DESCRIPTION
      : UpdateTaskSection.APP_VERSIONS

    const sectionFolder = section === UpdateTaskSection.APP_VERSIONS
      ? 'appVersions'
      : 'appDescription'

    return [{
      componentPath: `update/${sectionFolder}/step18`,
      stepOptions: {
        section,
        isLastStep: true,
        linkId: 'moderation',
        blockExecution: true
      }
    }]
  }

  steps = [
    {
      componentPath: 'Guide1',
      stepOptions: {
        section: UpdateTaskSection.GO_TO_GOOGLE
      }
    },
    {
      componentPath: `update/${updateSectionFolder}/step2`,
      stepOptions: {
        section: sectionUpdate
      }
    },
    ...getVariableSteps(),
    ...getLastSteps()
  ]

  return numberTaskSteps(UpdateTaskSection, steps)
}

function getRegistrationGuide(sectionObj, isShowGoogleAccountsSteps: boolean = true) {
  return [
    {
      componentPath: 'googleAccount/Guide1',
      stepOptions: {
        section: isShowGoogleAccountsSteps ? sectionObj.REG_GMAIL_ACCOUNT : null
      }
    },
    {
      componentPath: 'googleAccount/Guide2',
      stepOptions: {
        section: isShowGoogleAccountsSteps ? sectionObj.REG_GMAIL_ACCOUNT : null
      }
    },
    {
      componentPath: 'googleAccount/Guide3',
      stepOptions: {
        section: isShowGoogleAccountsSteps ? sectionObj.REG_GMAIL_ACCOUNT : null
      }
    },
    {
      componentPath: 'googleAccount/Guide4',
      stepOptions: {
        section: isShowGoogleAccountsSteps ? sectionObj.REG_GMAIL_ACCOUNT : null,
        blockExecution: true
      }
    },
    {
      componentPath: 'googleAccount/Guide8',
      stepOptions: {
        section: isShowGoogleAccountsSteps ? sectionObj.REG_GMAIL_ACCOUNT : null
      }
    },
    {
      componentPath: 'googleAccount/Guide9',
      stepOptions: {
        section: isShowGoogleAccountsSteps ? sectionObj.REG_GMAIL_ACCOUNT : null
      }
    },
    {
      componentPath: 'googleAccount/Guide10',
      stepOptions: {
        section: isShowGoogleAccountsSteps ? sectionObj.REG_GMAIL_ACCOUNT : null
      }
    },
  ]
}

function getPasswdGuide(sectionObj, isShowGoogleAccountsSteps: boolean = true) {
  return [
    {
      componentPath: 'googleAccount/Guide11',
      stepOptions: {
        section: isShowGoogleAccountsSteps ? sectionObj.RESERVE_PASSWD : null
      }
    },
    {
      componentPath: 'googleAccount/Guide12',
      stepOptions: {
        section: isShowGoogleAccountsSteps ? sectionObj.RESERVE_PASSWD : null
      }
    },
    {
      componentPath: 'googleAccount/Guide13',
      stepOptions: {
        section: isShowGoogleAccountsSteps ? sectionObj.RESERVE_PASSWD : null
      }
    },
    {
      componentPath: 'googleAccount/Guide14',
      stepOptions: {
        section: isShowGoogleAccountsSteps ? sectionObj.RESERVE_PASSWD : null,
        blockExecution: true
      }
    },
    {
      componentPath: 'googleAccount/Guide16',
      stepOptions: {
        section: isShowGoogleAccountsSteps ? sectionObj.RESERVE_PASSWD : null
      }
    },
    {
      componentPath: 'googleAccount/Guide17',
      stepOptions: {
        section: isShowGoogleAccountsSteps ? sectionObj.RESERVE_PASSWD : null
      }
    },
    {
      componentPath: 'googleAccount/Guide18',
      stepOptions: {
        section: isShowGoogleAccountsSteps ? sectionObj.RESERVE_PASSWD : null
      }
    },
    {
      componentPath: 'googleAccount/Guide19',
      stepOptions: {
        section: isShowGoogleAccountsSteps ? sectionObj.RESERVE_PASSWD : null,
        isLastStep: true,
        linkId: 'gmail',
        blockExecution: true
      }
    }
  ]
}

export function getStepsForOtherTask() {
  //  Формирование массива с настройками шагов инструкции для задачи создания Google аккаунта

  let steps

  steps = [
    ...getRegistrationGuide(GoogleAccountTaskSection),
    ...getPasswdGuide(GoogleAccountTaskSection)
  ]

  return numberTaskSteps(GoogleAccountTaskSection, steps)
}

export async function getGoogleAdminEmail(id: string | number) {
  //  Получение с бэка email для тестирования задачи

  try {
    const response = await axiosV3.post(`subtasks/${id}/google-account`)

    const email = response.data.email
    store.dispatch(setGoogleAdminEmail(email))
    return true
  } catch (e) {
    if (e.logout || e.offline || e.response.status === 404) return
    Notification({noticeType: 'warn', text: i18n.t('Get google admin email error')})
    console.info('Error in the method "getGoogleAdminEmail": ', e)
  }
}
